/**
 * 大会本部が配信するパイバル（測風気球）の観測結果を読み取る。
 *
 * 一関・平泉BF（2026-10）では、大会本部がオクレンジャー（一斉連絡サービス）で
 * 約20分おきに次のような本文を配信していた（docs/08 §198）:
 *
 *   Pibal Data released on 6:50 10th Oct      ← 題名
 *   一関平泉ＢＦ 宛て
 *   Surface Temp 11.0℃                         ← 朝だけ
 *   Alt(m) Deg(from) Spd(kt)
 *   100m 352° 5.4kt
 *   200m 351° 2.9kt
 *   …（100m刻み、最大1800m程度）
 *   in cloud                                   ← 雲で見失ったときだけ
 *   10/10 07:00                                ← 配信時刻
 *
 * 取り決め（利用者に確認済み）:
 * - 風向は真北基準の「吹いてくる向き」（Deg(from)）
 * - 高度はCLP（離陸地点）からの対地高度
 *
 * 実データで見た揺れ:
 * - 題名の時刻が `6:30` と `06:30` で混在
 * - `215°0.9 kt` や `1700m213 ° 10.3kt` のように空白の位置がずれた行
 * - 題名の日付の誤り（「14:15 10th Oct」が 10/09 14:37 に配信）。
 *   → 日付は題名ではなく配信時刻から取る
 *
 * 時刻はすべて会場の現地時刻の「壁時計」の文字列（YYYY-MM-DDTHH:MM）で持つ。
 * 午前・午後のまとまり（フライトと同じ分け方）もそこから決める。
 */

export interface OfficialPibalLevel {
  /** CLPからの対地高度 [m] */
  altAglM: number;
  /** 風が吹いてくる向き [度、真北]。無風（calm）ならnull */
  dirFromDeg: number | null;
  /** 風速 [kt] */
  speedKt: number;
}

export interface OfficialPibal {
  /** 同じ配信を見分ける鍵（配信時刻＋題名） */
  id: string;
  title: string;
  /** 放球時刻（現地、YYYY-MM-DDTHH:MM）。題名から読めなければ null */
  releasedLocal: string | null;
  /** 配信時刻（現地、YYYY-MM-DDTHH:MM） */
  sentLocal: string;
  /** 午前・午後のまとまり（YYYY-MM-DD-am / -pm） */
  session: string;
  /** 地表気温 [℃]。書かれていなければ null */
  surfaceTempC: number | null;
  /** 高度の低い順 */
  levels: OfficialPibalLevel[];
  /** 雲で見失った（「in cloud」） */
  inCloud: boolean;
  /** 読み取れなかったが中身のありそうな行（表示用にそのまま残す） */
  notes: string[];
}

export const KNOT_MPS = 1852 / 3600;
const FT_M = 0.3048;

/** 題名がパイバルの配信か */
export function isPibalTitle(title: string): boolean {
  return /pibal/i.test(title);
}

const pad2 = (n: number) => String(n).padStart(2, '0');

/**
 * 配信時刻 `MM/DD HH:MM` に年を補う。`reference`（今の現地の年月）より
 * 2か月以上先の月なら前年とみなす（年をまたぐ大会の1月に12月の配信を読む場合）
 */
function withYear(month: number, day: number, reference: { year: number; month: number }): string {
  const year = month - reference.month > 1 ? reference.year - 1 : reference.year;
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

/** 題名の「13:30 3rd Nov」から現地の日時（YYYY-MM-DDTHH:MM）。読めなければ null */
export function titleDateTime(title: string, reference: { year: number; month: number }): string | null {
  const m = /(\d{1,2}):(\d{2})\s+(\d{1,2})(?:st|nd|rd|th)?\s+([A-Za-z]{3})/i.exec(title);
  if (!m) return null;
  const month = MONTHS.indexOf(m[4].toLowerCase()) + 1;
  const day = Number(m[3]);
  if (month === 0 || day < 1 || day > 31 || Number(m[1]) > 23) return null;
  return `${withYear(month, day, reference)}T${pad2(Number(m[1]))}:${m[2]}`;
}

/** 午前・午後のまとまり。正午より前は午前 */
export function pibalSessionOf(localDateTime: string): string {
  const hour = Number(localDateTime.slice(11, 13));
  return `${localDateTime.slice(0, 10)}-${hour < 12 ? 'am' : 'pm'}`;
}

/** まとまりの表示名（例「10/10 午前」） */
export function pibalSessionLabel(session: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})-(am|pm)$/.exec(session);
  if (!m) return session;
  return `${Number(m[2])}/${Number(m[3])} ${m[4] === 'am' ? '午前' : '午後'}`;
}

// 高度と風向の間は「単位（m・ft）」か空白で区切る（`1700m213 ° 10.3kt`のように詰まった行もある）
const LEVEL_RE = /^(\d{1,5}(?:\.\d+)?)(?:\s*(m|ft)\s*|\s+)(\d{1,3}(?:\.\d+)?)(?:\s*[°º˚度]\s*|\s+)(\d{1,3}(?:\.\d+)?)\s*(kt|kts|knots?|m\/s|mps)?\.?$/i;
const CALM_RE = /^(\d{1,5}(?:\.\d+)?)(?:\s*(m|ft)\s*|\s+)calm\b/i;
const HEADER_RE = /alt\s*\(/i;
const SENT_RE = /^(\d{1,2})\/(\d{1,2})\s+(\d{1,2}):(\d{2})$/;
const TITLE_TIME_RE = /released\s+on\s+(\d{1,2}):(\d{2})/i;
const TEMP_RE = /surface\s*temp\.?\s*[:：]?\s*(-?\d+(?:\.\d+)?)\s*(?:℃|°\s*C|C)?/i;

/**
 * パイバルの本文を読む。`lines`は題名から配信時刻までの行（空行は無視する）。
 * 題名が無ければ（貼り付けで本文だけのとき）`options.title`、配信時刻が
 * 無ければ `options.fallbackSentLocal` を使う。高度の行が1つも無ければ null。
 */
export function parseOfficialPibal(
  input: string | string[],
  options: {
    title?: string;
    /** 今の現地の年月（配信時刻に年を補うのに使う） */
    reference: { year: number; month: number };
    /** 本文に配信時刻が無いとき（貼り付け）に使う現地時刻 YYYY-MM-DDTHH:MM */
    fallbackSentLocal?: string;
  },
): OfficialPibal | null {
  const lines = (Array.isArray(input) ? input : input.split(/\r?\n/))
    .map((l) => l.replace(/[　 ]/g, ' ').replace(/\s+/g, ' ').trim())
    .filter((l) => l !== '');
  let title = options.title ?? '';
  let altUnit: 'm' | 'ft' = 'm';
  let speedUnit: 'kt' | 'mps' = 'kt';
  let surfaceTempC: number | null = null;
  let inCloud = false;
  let sent: { month: number; day: number; hour: number; minute: number } | null = null;
  const levels: OfficialPibalLevel[] = [];
  const notes: string[] = [];

  for (const line of lines) {
    if (!title && isPibalTitle(line)) {
      title = line;
      continue;
    }
    if (line === title) continue;
    const sentMatch = SENT_RE.exec(line);
    if (sentMatch) {
      sent = { month: +sentMatch[1], day: +sentMatch[2], hour: +sentMatch[3], minute: +sentMatch[4] };
      continue;
    }
    if (HEADER_RE.test(line)) {
      if (/alt\s*\(\s*ft/i.test(line)) altUnit = 'ft';
      if (/m\s*\/\s*s/i.test(line)) speedUnit = 'mps';
      continue;
    }
    const temp = TEMP_RE.exec(line);
    if (temp) {
      surfaceTempC = Number(temp[1]);
      continue;
    }
    if (/in\s*cloud/i.test(line)) {
      inCloud = true;
      continue;
    }
    const calm = CALM_RE.exec(line);
    if (calm) {
      const alt = Number(calm[1]) * ((calm[2] ?? altUnit).toLowerCase() === 'ft' ? FT_M : 1);
      levels.push({ altAglM: alt, dirFromDeg: null, speedKt: 0 });
      continue;
    }
    const lv = LEVEL_RE.exec(line);
    if (lv) {
      const alt = Number(lv[1]) * ((lv[2] ?? altUnit).toLowerCase() === 'ft' ? FT_M : 1);
      const dir = Number(lv[3]) % 360;
      const unit = (lv[5] ?? '').toLowerCase();
      const isMps = unit === 'm/s' || unit === 'mps' || (unit === '' && speedUnit === 'mps');
      const speedKt = isMps ? Number(lv[4]) / KNOT_MPS : Number(lv[4]);
      levels.push({ altAglM: Math.round(alt * 10) / 10, dirFromDeg: dir, speedKt: Math.round(speedKt * 100) / 100 });
      continue;
    }
    // 宛先（「〇〇 宛て」）は捨てる。それ以外は表示用に残す
    if (/宛て?$/.test(line)) continue;
    notes.push(line);
  }
  if (levels.length === 0) return null;
  levels.sort((a, b) => a.altAglM - b.altAglM);

  let sentLocal: string;
  if (sent) {
    sentLocal = `${withYear(sent.month, sent.day, options.reference)}T${pad2(sent.hour)}:${pad2(sent.minute)}`;
  } else if (options.fallbackSentLocal) {
    sentLocal = options.fallbackSentLocal;
    // 本文に配信時刻が無い（WhatsAppの1件コピー・あとから共有）とき、題名の日時
    // （「13:30 3rd Nov」）が手がかりの日と違えば題名を信じる。あとから共有された
    // 前の日の配信が今日の配信として並ばないように（docs/08 §202）
    const td = titleDateTime(title, options.reference);
    if (td && td.slice(0, 10) !== sentLocal.slice(0, 10) && td <= sentLocal) sentLocal = td;
  } else {
    return null;
  }
  const tt = TITLE_TIME_RE.exec(title);
  let releasedLocal: string | null = null;
  if (tt) {
    const h = Number(tt[1]);
    const m = Number(tt[2]);
    if (h < 24 && m < 60) releasedLocal = `${sentLocal.slice(0, 10)}T${pad2(h)}:${pad2(m)}`;
    // 放球が配信より後になることは無い（日付をまたいだ深夜は扱わない）
    if (releasedLocal && releasedLocal > sentLocal) releasedLocal = null;
  }
  return {
    id: `${sentLocal}|${title}`,
    title,
    releasedLocal,
    sentLocal,
    session: pibalSessionOf(releasedLocal ?? sentLocal),
    surfaceTempC,
    levels,
    inCloud,
    notes,
  };
}

/** 新しい順（放球時刻、無ければ配信時刻） */
export function sortPibalsNewestFirst<T extends Pick<OfficialPibal, 'releasedLocal' | 'sentLocal'>>(list: T[]): T[] {
  const key = (p: T) => `${p.releasedLocal ?? p.sentLocal}|${p.sentLocal}`;
  return [...list].sort((a, b) => (key(a) < key(b) ? 1 : key(a) > key(b) ? -1 : 0));
}
